// Teleconsulta FASE 2 (script 390): prepago, satisfacción e indicadores.
// - ModalPagoTele (paciente/portal): De Una QR con polling (adaptador POS,
//   simulado hasta credenciales) o transferencia con comprobante (queda POR
//   VALIDAR hasta que el staff apruebe). Al pagar → factura SRI automática.
// - PagoStaff (médico/staff): validar transferencia (ver comprobante) o marcar
//   pagada/convenio/exonerada manualmente.
// - EncuestaTele: satisfacción 1-5 + comentario (paciente y médico, KPI 7.3.4).
// - IndicadoresTele: tablero med_tele_kpis para la Norma de Telesalud.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeDollarSign, Loader2, QrCode, Star, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { AutoTextarea, Campo, ImageUpload, Modal, fieldCls, medRpc } from './shared';

export const PAGO_ESTADOS: Record<string, { label: string; cls: string }> = {
  pendiente: { label: 'Pago pendiente', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  por_validar: { label: 'Pago por validar', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  pagado: { label: 'Pagada', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  convenio: { label: 'Convenio', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
  exonerado: { label: 'Exonerada', cls: 'bg-muted text-muted-foreground' },
};

export function PagoBadge({ estado, tarifa }: { estado?: string | null; tarifa?: number | string | null }) {
  if (!estado) return null;
  const e = PAGO_ESTADOS[estado] ?? PAGO_ESTADOS.pendiente;
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs ${e.cls} ${estado === 'por_validar' ? 'animate-pulse' : ''}`}>
      {e.label}{tarifa != null && Number(tarifa) > 0 ? ` · $${Number(tarifa).toFixed(2)}` : ''}
    </span>
  );
}

interface DeunaCobro { id: string; orden: string; estado: string; monto: number; qr: string | null; deep_link: string | null; referencia_pago?: string | null; pago_estado?: string }

// ---------------------------------------------------------------------------
// Modal de pago del PACIENTE (De Una QR | transferencia con comprobante)
// ---------------------------------------------------------------------------
export function ModalPagoTele({ citaId, tarifa, onClose, onPagado }: {
  citaId: string; tarifa: number; onClose: () => void; onPagado: () => void;
}) {
  const [via, setVia] = useState<'deuna' | 'transferencia'>('deuna');
  const [cobro, setCobro] = useState<DeunaCobro | null>(null);
  const [comprobante, setComprobante] = useState('');
  const [ref, setRef] = useState('');

  const generarQR = useMutation({
    mutationFn: () => medRpc<DeunaCobro>('med_tele_pagar_deuna', { _cita_id: citaId }),
    onSuccess: (d) => setCobro(d),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // polling del estado del QR cada 3 s hasta pagado/expirado
  useEffect(() => {
    if (!cobro || cobro.estado === 'pagado' || cobro.estado === 'expirado') return undefined;
    const t = setInterval(() => {
      medRpc<DeunaCobro>('med_tele_pagar_deuna_estado', { _cita_id: citaId })
        .then((d) => {
          setCobro((prev) => ({ ...prev, ...d }));
          if (d.pago_estado === 'pagado') { toast.success('Pago confirmado — ¡gracias!'); onPagado(); }
        })
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cobro?.id, cobro?.estado]);

  const transferir = useMutation({
    mutationFn: () => medRpc('med_tele_pagar_transferencia', { _cita_id: citaId, _comprobante: comprobante, _ref: ref || null }),
    onSuccess: () => { toast.success('Comprobante enviado — el centro médico validará tu pago'); onPagado(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const tab = (k: 'deuna' | 'transferencia', l: string) => (
    <button type="button" onClick={() => setVia(k)}
      className={`rounded-md px-3 py-1.5 text-sm ${via === k ? 'bg-primary text-primary-foreground' : 'border text-muted-foreground hover:bg-muted'}`}>
      {l}
    </button>
  );

  return (
    <Modal title={`Pagar teleconsulta · $${tarifa.toFixed(2)}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="flex gap-2">{tab('deuna', 'De Una (QR)')}{tab('transferencia', 'Transferencia')}</div>

        {via === 'deuna' && (
          <div className="flex flex-col items-center gap-3 py-2">
            {!cobro ? (
              <>
                <p className="text-center text-muted-foreground">Genera el código QR y escanéalo desde tu app De Una / Banco Pichincha.</p>
                <Button isLoading={generarQR.isPending} onClick={() => generarQR.mutate()}><QrCode className="w-4 h-4 mr-1" /> Generar QR</Button>
              </>
            ) : cobro.estado === 'pagado' ? (
              <p className="font-medium text-estable">✓ Pago recibido (ref. {cobro.referencia_pago})</p>
            ) : cobro.estado === 'expirado' ? (
              <>
                <p className="text-amber-600">El QR expiró.</p>
                <Button isLoading={generarQR.isPending} onClick={() => generarQR.mutate()}>Generar uno nuevo</Button>
              </>
            ) : (
              <>
                {cobro.qr
                  ? <img src={cobro.qr} alt={`QR De Una ${cobro.orden}`} className="w-52 rounded-md border" />
                  : <Loader2 className="w-6 h-6 animate-spin text-primary" />}
                <p className="text-xs text-muted-foreground">Orden {cobro.orden} · esperando el pago…</p>
              </>
            )}
          </div>
        )}

        {via === 'transferencia' && (
          <div className="space-y-3">
            <p className="text-muted-foreground">Realiza la transferencia y sube el comprobante; el centro médico validará tu pago antes de la consulta.</p>
            <ImageUpload value={comprobante} onChange={setComprobante} label="Comprobante de la transferencia *" ancho />
            <Campo label="N° de referencia (opcional)">
              <input className={fieldCls} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="N° de documento de la transferencia" />
            </Campo>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button isLoading={transferir.isPending} disabled={!comprobante} onClick={() => transferir.mutate()}>
                <Upload className="w-4 h-4 mr-1" /> Enviar comprobante
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Acciones de pago del STAFF (en la sala del médico)
// ---------------------------------------------------------------------------
interface PagoInfo { cita_id: string; tarifa: number | null; pago_estado: string | null; pago_metodo: string | null; pago_ref: string | null; pago_at: string | null; pago_comprobante: string | null; facturada: boolean }

export function PagoStaff({ citaId, onCambio }: { citaId: string; onCambio: () => void }) {
  const qc = useQueryClient();
  const { data: p } = useQuery<PagoInfo>({
    queryKey: ['med-tele-pago', citaId],
    queryFn: () => medRpc<PagoInfo>('med_tele_pago_info', { _cita_id: citaId }),
    refetchInterval: 8000,
  });
  const [verComp, setVerComp] = useState(false);
  const [marcar, setMarcar] = useState('pagado');
  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['med-tele-pago', citaId] }); onCambio(); };

  const validar = useMutation({
    mutationFn: (aprobar: boolean) => medRpc('med_tele_pago_validar', { _cita_id: citaId, _aprobar: aprobar }),
    onSuccess: (_d, aprobar) => { toast.success(aprobar ? 'Pago validado — factura emitida' : 'Pago rechazado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const marcarMut = useMutation({
    mutationFn: () => medRpc('med_tele_pago_marcar', { _cita_id: citaId, _estado: marcar, _ref: null }),
    onSuccess: () => { toast.success('Pago actualizado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!p || p.pago_estado == null) return null;
  return (
    <div className="rounded-md border px-3 py-2 space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <BadgeDollarSign className="w-4 h-4 text-muted-foreground" />
        <PagoBadge estado={p.pago_estado} tarifa={p.tarifa} />
        {p.pago_metodo && <span className="text-muted-foreground">vía {p.pago_metodo}{p.pago_ref ? ` · ref ${p.pago_ref}` : ''}</span>}
        {p.facturada && <span className="text-estable">· facturada</span>}
      </div>
      {p.pago_estado === 'por_validar' && (
        <div className="flex flex-wrap gap-2">
          {p.pago_comprobante && <Button size="sm" variant="outline" onClick={() => setVerComp(true)}>Ver comprobante</Button>}
          <Button size="sm" isLoading={validar.isPending} onClick={() => validar.mutate(true)}>Aprobar pago</Button>
          <Button size="sm" variant="outline" className="text-red-500" isLoading={validar.isPending} onClick={() => validar.mutate(false)}>Rechazar</Button>
        </div>
      )}
      {(p.pago_estado === 'pendiente' || p.pago_estado === 'por_validar') && (
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} h-8 w-auto text-xs`} value={marcar} onChange={(e) => setMarcar(e.target.value)}>
            <option value="pagado">Pagada (ventanilla)</option>
            <option value="convenio">Convenio</option>
            <option value="exonerado">Exonerada</option>
          </select>
          <Button size="sm" variant="outline" isLoading={marcarMut.isPending} onClick={() => marcarMut.mutate()}>Marcar</Button>
        </div>
      )}
      {verComp && p.pago_comprobante && (
        <Modal title="Comprobante de transferencia" onClose={() => setVerComp(false)}>
          <img src={p.pago_comprobante} alt="Comprobante" className="max-h-[70vh] w-full object-contain rounded-md border" />
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Encuesta de satisfacción (paciente y médico) — KPI 7.3.4
// ---------------------------------------------------------------------------
export function EncuestaTele({ teleconsultaId, titulo, onListo }: { teleconsultaId: string; titulo?: string; onListo: () => void }) {
  const [puntaje, setPuntaje] = useState(0);
  const [comentario, setComentario] = useState('');
  const enviar = useMutation({
    mutationFn: () => medRpc('med_tele_satisfaccion', { _id: teleconsultaId, _puntaje: puntaje, _comentario: comentario || null }),
    onSuccess: () => { toast.success('¡Gracias por tu calificación!'); onListo(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title={titulo ?? '¿Cómo estuvo tu teleconsulta?'} onClose={onListo}>
      <div className="space-y-3 text-sm">
        <div className="flex justify-center gap-1.5 py-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" title={`${n} estrella${n > 1 ? 's' : ''}`} aria-label={`${n} estrellas`} onClick={() => setPuntaje(n)}>
              <Star className={`w-8 h-8 ${n <= puntaje ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
            </button>
          ))}
        </div>
        <AutoTextarea placeholder="Comentario (opcional)…" value={comentario} onChange={(e) => setComentario(e.target.value)} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onListo}>Omitir</Button>
          <Button disabled={puntaje === 0} isLoading={enviar.isPending} onClick={() => enviar.mutate()}>Enviar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Indicadores de telesalud (Norma A.M. 00044-2025, numeral 7.3.4) — staff
// ---------------------------------------------------------------------------
interface KpisTele {
  rango: { desde: string; hasta: string };
  total: number; finalizadas: number; efectivas: number; parciales: number; fallidas: number; no_show: number;
  duracion_prom_min: number | null; espera_prom_min: number | null;
  reconexiones: number; llamadas_con_reconexion: number; eventos_adversos: number;
  calidad: Record<string, number>;
  satisfaccion_paciente: { promedio: number; respuestas: number } | null;
  satisfaccion_medico: { promedio: number; respuestas: number } | null;
  pagos: { pagadas: number; monto: number; pendientes: number; convenio_exoneradas: number };
  por_medico: { medico: string; total: number; efectivas: number; duracion_min: number | null }[];
}

const primerDiaMes = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const hoyIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export function IndicadoresTele() {
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(hoyIso());
  const { data: k, isFetching } = useQuery<KpisTele>({
    queryKey: ['med-tele-kpis', desde, hasta],
    queryFn: () => medRpc<KpisTele>('med_tele_kpis', { _desde: desde, _hasta: hasta }),
  });

  const tarjeta = (titulo: string, valor: React.ReactNode, sub?: string) => (
    <Card><CardContent className="pt-4 pb-3">
      <p className="text-xs text-muted-foreground">{titulo}</p>
      <p className="text-2xl font-semibold tabular-nums">{valor}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </CardContent></Card>
  );
  const pct = (n: number) => (k && k.total > 0 ? ` (${Math.round((n / k.total) * 100)}%)` : '');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Indicadores de telesalud (A.M. 00044-2025 · 7.3.4)</span>
        <input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} />
        <span className="text-muted-foreground">→</span>
        <input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} />
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>
      {k && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {tarjeta('Teleconsultas', k.total, `${k.finalizadas} finalizadas`)}
            {tarjeta('Efectivas', `${k.efectivas}${pct(k.efectivas)}`)}
            {tarjeta('Parciales / Fallidas', `${k.parciales} / ${k.fallidas}`)}
            {tarjeta('No-show', k.no_show, 'sin check-in')}
            {tarjeta('Duración promedio', k.duracion_prom_min != null ? `${k.duracion_prom_min} min` : '—')}
            {tarjeta('Espera en sala', k.espera_prom_min != null ? `${k.espera_prom_min} min` : '—', 'check-in → admisión')}
            {tarjeta('Reconexiones', k.reconexiones, `${k.llamadas_con_reconexion} llamadas afectadas`)}
            {tarjeta('Eventos adversos', k.eventos_adversos)}
            {tarjeta('Satisfacción paciente', k.satisfaccion_paciente ? `${k.satisfaccion_paciente.promedio} ★` : '—',
              k.satisfaccion_paciente ? `${k.satisfaccion_paciente.respuestas} respuestas` : 'sin respuestas')}
            {tarjeta('Satisfacción médico', k.satisfaccion_medico ? `${k.satisfaccion_medico.promedio} ★` : '—',
              k.satisfaccion_medico ? `${k.satisfaccion_medico.respuestas} respuestas` : 'sin respuestas')}
            {tarjeta('Pagadas', `${k.pagos.pagadas} · $${Number(k.pagos.monto).toFixed(2)}`,
              `${k.pagos.pendientes} pendientes · ${k.pagos.convenio_exoneradas} convenio/exon.`)}
            {tarjeta('Calidad reportada', Object.entries(k.calidad).map(([c, n]) => `${c}: ${n}`).join(' · ') || '—')}
          </div>
          <Card><CardContent className="pt-4 overflow-x-auto">
            <p className="text-sm font-medium mb-2">Por médico</p>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3">N°</th><th className="py-1.5 pr-3">Médico</th><th className="py-1.5 pr-3">Teleconsultas</th>
                <th className="py-1.5 pr-3">Efectivas</th><th className="py-1.5 pr-3">Duración prom.</th>
              </tr></thead>
              <tbody>
                {k.por_medico.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">Sin datos en el rango</td></tr>}
                {k.por_medico.map((m, i) => (
                  <tr key={m.medico} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3">{m.medico}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{m.total}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{m.efectivas}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{m.duracion_min != null ? `${m.duracion_min} min` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
